// brand-tarot-game.js — "El juego de tarot de los sueños".
//
// Light-DOM custom element that enhances the server-rendered <brand-tarot-game> setup
// form into a full interactive tarot experience: shuffle (Fisher-Yates), pick face-down
// cards one by one, flip-reveal them, and interpret them with a pure-JS engine driven by
// assets/tarot-data.json.
//
// Constraints honored (CLAUDE.md): no frameworks/deps, guarded customElements.define,
// progressive enhancement (no-JS form + CTA still work), data fetched on the FIRST user
// gesture (never on load), tiny delegated handlers (INP-safe), a11y (radiogroups, focus
// management, aria-live results, keyboard-native <button> deck).

if (!customElements.get('brand-tarot-game')) {
  const TEMA_LABEL = {
    general: 'General',
    amor: 'Amor',
    dinero: 'Dinero',
    trabajo: 'Trabajo',
    salud: 'Salud',
    espiritual: 'Espiritual',
  };

  // Aperturas en la voz de Beth (ver MuestrasAudiosLecturas/voz-y-metodo-beth.md):
  // saludo cálido "Hola, corazón", tuteo, "vamos a ver qué hay / qué se manifiesta".
  const OPENERS = {
    general: 'Hola, corazón. Respira hondo, que ya me acomodé las cartas para ti. Vamos a ver qué hay.',
    amor: 'Hola, corazón. Vamos a ver qué se manifiesta en tu vida amorosa; deja que el corazón hable.',
    dinero: 'Hola, corazón. Vamos a ver cómo anda tu parte económica, con calma y sin juicios.',
    trabajo: 'Hola, corazón. Vamos a ver qué hay para ti en el trabajo y en tus proyectos.',
    salud: 'Hola, corazón. Tu energía habla antes que las palabras; vamos a escucharla con cariño.',
    espiritual: 'Hola, corazón. Se afina el velo; vamos a ver qué te quiere decir tu parte espiritual.',
  };

  // Cómo Beth "hace hablar" a la carta (la carta es la emisora).
  const SAYS = ['me dice', 'me habla de', 'me anuncia', 'me está diciendo', 'me viene a decir'];

  // Bendición de arcángel hacia el cierre (rasgo distintivo de su método).
  const ARCANGELES = [
    { n: 'Rafael', m: 'que respires hondo y sanes lo que traes cargando' },
    { n: 'Jofiel', m: 'que salgas, tomes aire fresco y dejes entrar ideas nuevas' },
    { n: 'Uriel', m: 'que atiendas los pensamientos que te llegan; son oraciones gozosas' },
    { n: 'Jeremías', m: 'que hagas un balance sereno de tu vida' },
    { n: 'Raguel', m: 'que mires más allá de la soledad; todo se muestra como debe de ser' },
  ];

  const AMOR_CIERRE = 'Es lo que me dice el tarot del amor para ti.';
  const FIRMA = 'Toma lo que te resuene, corazón; lo que no, déjalo ir. El tarot es únicamente una guía.';

  // Cards that soften a "sí" into a "todavía no" for the Sí/No spread.
  const SI_NO_CHALLENGERS = new Set([
    'la-torre', 'la-muerte', 'el-diablo', 'el-colgado',
    'cinco-de-oros', 'cinco-de-copas',
    'tres-de-espadas', 'cinco-de-espadas', 'siete-de-espadas',
    'ocho-de-espadas', 'nueve-de-espadas', 'diez-de-espadas',
  ]);

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };

  const rand = (n) => Math.floor(Math.random() * n);
  const pickOne = (arr) => arr[rand(arr.length)];

  customElements.define(
    'brand-tarot-game',
    class extends HTMLElement {
      connectedCallback() {
        this.setup = this.querySelector('[data-tarot-setup]');
        this.stage = this.querySelector('[data-tarot-stage]');
        this.result = this.querySelector('[data-tarot-result]');
        if (!this.setup || !this.stage || !this.result) return;

        this.src = this.dataset.src;
        this.cardUrl = this.dataset.cardUrl || '';
        this.showQuestion = this.dataset.showQuestion === 'true';
        this.ctaUrl = this.dataset.ctaUrl || '';
        this.ctaLabel = this.dataset.ctaLabel || 'Agenda una lectura real';
        // WhatsApp CTA (number reused from the global brand setting) + catalog CTA.
        this.waNumber = (this.dataset.waNumber || '').replace(/\D/g, '');
        this.waMessage = this.dataset.waMessage || '¡Hola, Bethsotérica! ✨ Acabo de hacer una tirada de tarot y quiero una lectura real.';
        this.waLabel = this.dataset.waLabel || 'Consultar por WhatsApp';
        this.catalogUrl = this.dataset.catalogUrl || '/collections/all';
        this.catalogLabel = this.dataset.catalogLabel || 'Ver el catálogo';
        // Conversion-panel copy (editable via schema).
        this.ctaHeading = this.dataset.ctaHeading || 'Esto es solo un asomo, corazón.';
        this.ctaBody = this.dataset.ctaBody
          || 'La lectura de verdad te la hago yo, para ti: con tu nombre y tu fecha, carta por carta y en audio. Escríbeme y vemos qué hay para ti.';

        this.data = null;
        this._dataPromise = null;

        // JS is alive → hide the no-JS fallback notice (CSS keys off .is-enhanced).
        this.classList.add('is-enhanced');

        // Fetch on the FIRST user gesture, not on load.
        const primeFetch = () => this.ensureData();
        this.setup.addEventListener('focusin', primeFetch, { once: true });
        this.setup.addEventListener('pointerdown', primeFetch, { once: true });

        // Refine tirada availability when the tema changes.
        this.setup.addEventListener('change', (e) => {
          if (e.target.name === 'tarot_tema') this.syncAffinity();
        });

        this.setup.addEventListener('submit', (e) => {
          e.preventDefault();
          this.start();
        });
      }

      ensureData() {
        if (this._dataPromise) return this._dataPromise;
        this._dataPromise = fetch(this.src)
          .then((r) => {
            if (!r.ok) throw new Error('tarot-data ' + r.status);
            return r.json();
          })
          .then((json) => {
            this.data = json;
            this.syncAffinity();
            return json;
          })
          .catch((err) => {
            this._dataPromise = null; // allow retry
            throw err;
          });
        return this._dataPromise;
      }

      // Disable spreads that are not affine to the selected tema (data-driven).
      syncAffinity() {
        if (!this.data) return;
        const tema = this.getTema();
        const labels = this.setup.querySelectorAll('[data-tirada]');
        let checkedDisabled = false;
        let firstEnabled = null;

        labels.forEach((label) => {
          const spread = this.data.tiradas.find((t) => t.slug === label.dataset.tirada);
          const input = label.querySelector('input');
          const card = label.querySelector('.tg-tirada__card');
          const ok = spread && spread.temas_afines.includes(tema);
          input.disabled = !ok;
          label.classList.toggle('is-disabled', !ok);
          if (card) card.setAttribute('aria-disabled', ok ? 'false' : 'true');
          if (ok && !firstEnabled) firstEnabled = input;
          if (!ok && input.checked) checkedDisabled = true;
        });

        // If the checked spread got disabled, move the selection to the first valid one.
        if (checkedDisabled && firstEnabled) firstEnabled.checked = true;
      }

      getTema() {
        const r = this.setup.querySelector('input[name="tarot_tema"]:checked');
        return (r && r.value) || 'general';
      }

      getTiradaSlug() {
        const r = this.setup.querySelector('input[name="tarot_tirada"]:checked');
        return (r && r.value) || 'carta-del-dia';
      }

      getQuestion() {
        const q = this.setup.querySelector('textarea[name="tarot_pregunta"]');
        return (q && q.value.trim()) || '';
      }

      start() {
        this.ensureData()
          .then(() => this.beginReading())
          .catch(() => this.showError());
      }

      showError() {
        this.result.hidden = false;
        this.result.textContent = '';
        this.result.appendChild(
          el('p', 'tg-result__question', 'No pudimos preparar el mazo en este momento. Recarga la página e inténtalo de nuevo.')
        );
      }

      // ── Build the deck + stage ──────────────────────────────────────────────
      beginReading() {
        const tema = this.getTema();
        const spread = this.data.tiradas.find((t) => t.slug === this.getTiradaSlug());
        if (!spread) return this.showError();

        this.session = { tema, spread, question: this.getQuestion(), picks: [] };

        // Inverted policy (reglas.invertidas): single-card spreads stay upright, except
        // Sí/No which uses inversion for the "todavía no" nuance; 3+ spreads may invert.
        const allowInvert = spread.slug === 'si-o-no' || spread.num_cartas >= 3;

        const order = this.data.cartas.map((_, i) => i);
        for (let i = order.length - 1; i > 0; i--) {
          const j = rand(i + 1);
          [order[i], order[j]] = [order[j], order[i]];
        }
        this.session.deck = order.map((cardIndex) => ({
          cardIndex,
          inverted: allowInvert && Math.random() < 1 / 3,
        }));

        this.renderStage();
      }

      renderStage() {
        const { spread } = this.session;
        const need = spread.num_cartas;
        const poolSize = Math.min(this.session.deck.length, Math.max(12, need + 7));

        this.setup.hidden = true;
        this.result.hidden = true;
        this.result.textContent = '';
        this.stage.hidden = false;
        this.stage.textContent = '';

        const instr = el('p', 'tg-stage__instruction');
        instr.tabIndex = -1;
        instr.textContent =
          need === 1
            ? 'Concéntrate en tu intención y elige tu carta.'
            : `Concéntrate en tu intención y elige tus ${need} cartas, una a una.`;
        this.stage.appendChild(instr);

        this.counter = el('p', 'tg-stage__counter');
        this.counter.setAttribute('aria-live', 'polite');
        this.updateCounter();
        this.stage.appendChild(this.counter);

        const deck = el('div', 'tg-deck');
        deck.setAttribute('role', 'group');
        deck.setAttribute('aria-label', 'Mazo boca abajo');
        for (let i = 0; i < poolSize; i++) {
          const btn = el('button', 'tg-deck__btn');
          btn.type = 'button';
          btn.dataset.deckIndex = String(i);
          btn.setAttribute('aria-label', `Carta boca abajo ${i + 1} de ${poolSize}`);
          btn.innerHTML =
            '<span class="tarot-back" aria-hidden="true">' +
            '<svg class="tarot-back__emblem" viewBox="0 0 120 200" role="presentation" focusable="false">' +
            '<use href="#brand-tarot-back-emblem"></use></svg></span>';
          btn.addEventListener('click', () => this.pickCard(i, btn));
          deck.appendChild(btn);
        }
        this.stage.appendChild(deck);

        instr.focus();
      }

      updateCounter() {
        const { spread, picks } = this.session;
        this.counter.textContent = `Has elegido ${picks.length} de ${spread.num_cartas}`;
      }

      pickCard(deckIndex, btn) {
        const { spread, picks } = this.session;
        if (picks.length >= spread.num_cartas) return;
        if (picks.some((p) => p.deckIndex === deckIndex)) return;

        picks.push({ deckIndex, ...this.session.deck[deckIndex] });
        btn.classList.add('is-picked');
        btn.disabled = true;
        this.updateCounter();

        if (picks.length === spread.num_cartas) {
          // Disable the rest so late clicks do nothing.
          this.stage.querySelectorAll('.tg-deck__btn').forEach((b) => {
            b.disabled = true;
          });
          // Brief beat so the pick registers visually before the reveal.
          setTimeout(() => this.reveal(), 240);
        }
      }

      // ── Reveal + interpret ──────────────────────────────────────────────────
      reveal() {
        const { tema, spread, question, picks } = this.session;
        this.stage.hidden = true;
        this.result.hidden = false;
        this.result.textContent = '';

        const cards = picks.map((p, i) => ({
          carta: this.data.cartas[p.cardIndex],
          inverted: p.inverted,
          posicion: spread.posiciones[i],
        }));

        // Opening line (atmosphere).
        const opening = el('p', 'tg-result__opening');
        opening.tabIndex = -1;
        opening.textContent = OPENERS[tema] || OPENERS.general;
        this.result.appendChild(opening);

        if (question) {
          const q = el('p', 'tg-result__question');
          q.appendChild(el('span', null, 'Tu pregunta: '));
          const strong = el('strong', null, '“' + question + '”');
          q.appendChild(strong);
          this.result.appendChild(q);
        }

        // Card-by-card spread.
        const spreadEl = el('div', 'tg-spread' + (spread.num_cartas === 1 ? ' tg-spread--single' : ''));
        cards.forEach((c) => spreadEl.appendChild(this.renderCard(c, spread, tema)));
        this.result.appendChild(spreadEl);

        // Synthesis (predominancias / numerología) for 3+ card spreads.
        if (spread.num_cartas >= (this.data.reglas.predominancias.aplica_desde_cartas || 3)) {
          const synth = this.buildSynthesis(cards);
          if (synth) this.result.appendChild(synth);
        }

        // Cierre en la voz de Beth: (amor) tarot del amor → bendición de arcángel →
        // línea empoderadora → firma fija ("toma lo que te resuene… el tarot es una guía").
        if (tema === 'amor') this.result.appendChild(el('p', 'tg-closing__amor', AMOR_CIERRE));

        const ang = pickOne(ARCANGELES);
        this.result.appendChild(
          el('p', 'tg-closing__angel', 'El arcángel ' + ang.n + ' te acompaña: ' + ang.m + '.')
        );

        const closings = this.data.reglas.tono.frases_cierre_empoderadoras;
        this.result.appendChild(el('p', 'tg-closing', pickOne(closings)));

        this.result.appendChild(el('p', 'tg-closing__firma', FIRMA));

        const disc = this.data.reglas.tono.disclaimers_suaves || {};
        if (tema === 'salud' && disc.salud) this.result.appendChild(el('p', 'tg-disclaimer', disc.salud));
        else if (tema === 'dinero' && disc.dinero) this.result.appendChild(el('p', 'tg-disclaimer', disc.dinero));

        // ── CTA de conversión: el remate que empuja a la lectura real ──────────
        const cta = el('div', 'tg-cta');
        cta.appendChild(el('p', 'tg-cta__kicker', 'La lectura de verdad'));
        cta.appendChild(el('p', 'tg-cta__title', this.ctaHeading));
        cta.appendChild(el('p', 'tg-cta__body', this.ctaBody));

        const points = el('ul', 'tg-cta__points');
        [
          'Personalizada con tu nombre y tu fecha',
          'Te la mando en audio, como si platicáramos',
          'Con tus preguntas específicas respondidas',
        ].forEach((txt) => {
          const li = el('li', 'tg-cta__point');
          li.appendChild(el('span', 'tg-cta__ico', '✦'));
          li.appendChild(document.createTextNode(' ' + txt));
          points.appendChild(li);
        });
        cta.appendChild(points);

        // WhatsApp — botón grande y dominante; el mensaje lleva el contexto de la tirada.
        if (this.waNumber) {
          const ctx = this.waMessage
            + '\n\nTirada: ' + spread.nombre
            + '\nTema: ' + (tema || 'general')
            + (question ? '\nMi pregunta: "' + question + '"' : '');
          const wa = el('a', 'tg-cta__wa');
          wa.href = 'https://wa.me/' + this.waNumber + '?text=' + encodeURIComponent(ctx);
          wa.target = '_blank';
          wa.rel = 'noopener';
          const ico = el('span', 'tg-cta__waico');
          ico.setAttribute('aria-hidden', 'true');
          ico.innerHTML =
            '<svg viewBox="0 0 24 24" role="presentation" focusable="false">' +
            '<path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2m0 1.67c4.54 0 8.24 3.7 8.24 8.24 0 4.54-3.7 8.24-8.24 8.24-1.53 0-3.02-.42-4.31-1.22l-.31-.18-3.11.82.83-3.03-.2-.32a8.19 8.19 0 0 1-1.26-4.35c0-4.54 3.7-8.24 8.24-8.24m4.52 11.64c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28"/></svg>';
          wa.appendChild(ico);
          wa.appendChild(el('span', 'tg-cta__walabel', this.waLabel));
          cta.appendChild(wa);
        }

        // Secundarios atenuados: catálogo, CTA legado (si hay) y nueva tirada.
        const sec = el('div', 'tg-cta__secondary');
        const cat = el('a', 'tg-cta__link', this.catalogLabel);
        cat.href = this.catalogUrl;
        sec.appendChild(cat);

        if (this.ctaUrl && this.ctaUrl !== '#') {
          const cta2 = el('a', 'tg-cta__link', this.ctaLabel);
          cta2.href = this.ctaUrl;
          if (/^https?:/i.test(this.ctaUrl)) {
            cta2.target = '_blank';
            cta2.rel = 'noopener';
          }
          sec.appendChild(cta2);
        }

        const again = el('button', 'tg-cta__link', 'Nueva tirada');
        again.type = 'button';
        again.addEventListener('click', () => this.resetToSetup());
        sec.appendChild(again);

        cta.appendChild(sec);
        this.result.appendChild(cta);

        opening.focus();
      }

      renderCard({ carta, inverted, posicion }, spread, tema) {
        const wrap = el('article', 'tg-card');

        // Face: authentic RWS card art in the brand gold-foil finish (+ animated sheen via CSS).
        const face = el('div', 'tg-card__face' + (inverted ? ' is-inverted' : ''));
        const img = el('img', 'tg-card__img');
        img.src = this.cardUrl.replace('tarot-la-estrella.webp', 'tarot-' + carta.slug + '.webp');
        img.alt = carta.nombre + (inverted ? ' (invertida)' : '');
        img.width = 300;
        img.height = 527;
        img.loading = 'eager';
        img.decoding = 'async';
        // If a card-front asset is missing, degrade to the styled card face (gradient +
        // border) instead of a broken-image icon; the body text still names the card.
        img.addEventListener('error', () => { img.style.display = 'none'; });
        face.appendChild(img);
        if (inverted) face.appendChild(el('span', 'tg-card__invtag', 'Invertida'));
        wrap.appendChild(face);

        // Body.
        const body = el('div', 'tg-card__body');
        if (posicion) body.appendChild(el('p', 'tg-card__pos', posicion.nombre));

        const name = el('p', 'tg-card__name');
        name.appendChild(document.createTextNode(carta.nombre + ' '));
        const arcLabel =
          carta.arcano === 'mayor'
            ? 'Arcano mayor'
            : 'Arcano menor · ' + (carta.palo ? carta.palo : '');
        name.appendChild(el('span', null, '· ' + arcLabel.trim()));
        body.appendChild(name);

        // Lead en la voz de Beth: la carta es la emisora + cascada de palabras clave
        // ("El 8 de oros me habla de: trabajo, oficio, maestría.").
        const kws = (carta.palabras_clave || []).slice(0, 3).join(', ');
        const lead = carta.nombre + ' ' + pickOne(SAYS) + (kws ? ': ' + kws + '.' : '.');
        body.appendChild(el('p', 'tg-card__lead', lead));

        // Position meaning (regla de oro: la posición manda).
        if (posicion) {
          body.appendChild(
            this.textPara(posicion.nombre + '. ', posicion.significado_posicion)
          );
        }

        // Core meaning (upright / inverted) — fluye tras el lead, sin etiqueta redundante.
        body.appendChild(
          this.textPara('', inverted ? carta.invertida : carta.derecha)
        );

        // Inverted reframing (nunca mal augurio) — en su voz: "Ojo:".
        if (inverted) {
          body.appendChild(
            this.textPara('Ojo: ', this.data.reglas.invertidas.tono)
          );
        }

        // Thematic layer.
        if (tema !== 'general' && carta.temas && carta.temas[tema]) {
          body.appendChild(this.textPara(TEMA_LABEL[tema] + '. ', carta.temas[tema]));
        }

        // Sí/No verdict.
        if (spread.slug === 'si-o-no') {
          body.appendChild(this.textPara('Veredicto. ', this.siNoVerdict(carta, inverted)));
        }

        // Advice for single-card spreads — en su voz: "Mira, te digo:".
        if (spread.num_cartas === 1 && carta.consejo) {
          body.appendChild(this.textPara('Mira, te digo: ', carta.consejo));
        }

        wrap.appendChild(body);
        return wrap;
      }

      textPara(label, text) {
        const p = el('p', 'tg-card__text');
        p.appendChild(el('span', 'tg-card__label', label));
        p.appendChild(document.createTextNode(text));
        return p;
      }

      siNoVerdict(carta, inverted) {
        if (inverted) {
          return 'Todavía no… o mejor dicho: sí, pero primero resuelve lo que esta carta te muestra. La energía está, falta destrabarla.';
        }
        if (SI_NO_CHALLENGERS.has(carta.slug)) {
          return 'La balanza pide paciencia: hay algo que atender antes de que el sí llegue con fuerza.';
        }
        return 'La balanza se inclina al sí: la corriente te acompaña, avanza con intención.';
      }

      buildSynthesis(cards) {
        const reglas = this.data.reglas;
        const pred = reglas.predominancias;
        const total = cards.length;
        const paras = [];

        // Mayores vs cotidiano.
        const mayores = cards.filter((c) => c.carta.arcano === 'mayor').length;
        if (mayores / total >= (pred.mayores.umbral || 0.5)) paras.push(pred.mayores.texto);
        else if (mayores === 0) paras.push(pred.sin_mayores.texto);

        // Predominant palo (among arcanos menores).
        const paloCount = {};
        cards.forEach((c) => {
          if (c.carta.palo) paloCount[c.carta.palo] = (paloCount[c.carta.palo] || 0) + 1;
        });
        let topPalo = null;
        let topN = 0;
        Object.keys(paloCount).forEach((p) => {
          if (paloCount[p] > topN) {
            topN = paloCount[p];
            topPalo = p;
          }
        });
        if (topPalo && topN >= 2 && pred.palos[topPalo]) paras.push(pred.palos[topPalo].texto);

        // Court cards (figuras).
        const figuras = cards.filter(
          (c) => c.carta.arcano === 'menor' && c.carta.numero >= 11
        ).length;
        if (figuras >= 2 && pred.figuras) paras.push(pred.figuras.texto);

        // Numerology: a repeated number among minor arcana.
        const numCount = {};
        cards.forEach((c) => {
          if (c.carta.arcano === 'menor' && c.carta.numero <= 10) {
            numCount[c.carta.numero] = (numCount[c.carta.numero] || 0) + 1;
          }
        });
        const repeated = Object.keys(numCount).find((n) => numCount[n] >= 2);
        if (repeated && reglas.numerologia.por_numero[repeated]) {
          paras.push(reglas.numerologia.por_numero[repeated]);
        }

        if (!paras.length) return null;

        const box = el('div', 'tg-synth');
        box.appendChild(el('p', 'tg-synth__h', 'Lo que revela el conjunto'));
        paras.forEach((t) => box.appendChild(el('p', null, t)));
        return box;
      }

      resetToSetup() {
        this.result.hidden = true;
        this.result.textContent = '';
        this.stage.hidden = true;
        this.stage.textContent = '';
        this.setup.hidden = false;
        const firstLegend = this.setup.querySelector('.brand-tarot-game__legend');
        const startBtn = this.setup.querySelector('[data-tarot-start]');
        if (firstLegend) {
          firstLegend.setAttribute('tabindex', '-1');
          firstLegend.focus();
        } else if (startBtn) {
          startBtn.focus();
        }
      }
    }
  );
}
